import { Component, computed, Signal, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { SectionComponent } from 'src/app/shared/components/section/section.component';
import { SubsectionComponent } from 'src/app/shared/components/subsection/subsection.component';
import { CraftCalculatorStateService } from '../craft-calculator-state.service';
import { CraftGroupPersistenceHelper, DoneCraftPersistanceModel, fromDoneCraftPersistenceModel, toDoneCraftPersistenceModel } from 'src/app/shared/models/craft-models/craft-persistence-models';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { TopItemsService } from 'src/app/services/top-items/top-items.service';
import { finalize, take } from 'rxjs';
import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { AoDataGetterService } from 'src/app/services/ao-data-getter/ao-data-getter.service';
import { getAllLocations } from 'src/app/shared/constants/locations.constant';
import { getAllPriceTypes } from 'src/app/shared/constants/market.constant';
import { MatCardModule } from '@angular/material/card';
import { MatRippleModule } from '@angular/material/core';
import { CommonModule, DatePipe } from '@angular/common';
import { CraftGroup, DoneCraft } from 'src/app/shared/models/craft-models/craft-models';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from 'src/app/shared/components/dialog/dialog.component';
import { AfmBeService } from 'src/app/services/afm-be/afm-be.service';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ItemImageComponent } from 'src/app/shared/components/item-image/item-image.component';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { getServerName } from 'src/app/shared/constants/servers.constant';

@Component({
  selector: 'app-craft-calculator-load-save',
  imports: [
    CommonModule,
    RouterLink,
    SectionComponent,
    SubsectionComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatCardModule,
    MatRippleModule,
    DatePipe,
    MatIconModule,
    MatTooltipModule,
    ItemImageComponent,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
  ],
  templateUrl: './craft-calculator-load-save.component.html',
  styleUrl: './craft-calculator-load-save.component.scss',
})
export class CraftCalculatorLoadSaveComponent {
  public doneCraftsSort: Signal<MatSort | undefined> = viewChild<MatSort>('doneCraftsSort');
  public doneCraftsPaginator: Signal<MatPaginator | undefined> = viewChild<MatPaginator>('doneCraftsPaginator');
  getServerName = getServerName;

  sortedDoneCrafts = computed(() => {
    return this.craftCalculatorStateService.userDoneCrafts().sort((a, b) => {
      const dateA = a.craftDate ? new Date(a.craftDate) : new Date();
      const dateB = b.craftDate ? new Date(b.craftDate) : new Date();
      return dateB.getTime() - dateA.getTime();
    });
  });

  public doneCraftsDataSource: Signal<MatTableDataSource<DoneCraft>> = computed(() => {
    const ds = this.setupDoneCraftsDataSource();
    return ds;
  });

  public doneCraftsDisplayedColumns: Signal<string[]> = computed(() => {
    return ['items', 'name', 'server', 'totalIncome', 'totalExpenses', 'totalInputWeight', 'totalOutputWeight', 'totalFame', 'totalFocus', 'profitPerFocus', 'totalCraftFee', 'profit', 'craftDate', 'actions'];
  });
  constructor(
    public craftCalculatorStateService: CraftCalculatorStateService,
    private topItemsService: TopItemsService,
    private jsonReaderService: JsonReaderService,
    private aoDataGetterService: AoDataGetterService,
    private matDialog: MatDialog,
    public afmBeService: AfmBeService,
    private snackBar: MatSnackBar
  ) {}

  trackByDoneCraftId(index: number, doneCraft: DoneCraft): string | number {
    return doneCraft.id || index;
  }

  private setupDoneCraftsDataSource(): MatTableDataSource<DoneCraft> {
    const doneCrafts = this.sortedDoneCrafts();
    const ds = new MatTableDataSource<DoneCraft>(doneCrafts);

    const doneCraftsSort = this.doneCraftsSort();

    if (doneCraftsSort) {
      ds.sortingDataAccessor = (item: DoneCraft, property) => {
        switch (property) {
          case 'name':
            return item.craftGroup.name();
          case 'server':
            return item.craftGroup.server();
          case 'totalIncome':
            return item.totalIncome;
          case 'totalExpenses':
            return item.totalExpenses;
          case 'totalInputWeight':
            return item.totalInputWeight;
          case 'totalOutputWeight':
            return item.totalOutputWeight;
          case 'totalFame':
            return item.totalCraftFamePlayerTotal;
          case 'totalFocus':
            return item.totalFocus;
          case 'profitPerFocus':
            return item.totalFocus > 0 ? item.totalResult / item.totalFocus : 0;
          case 'totalCraftFee':
            return item.totalFee;
          case 'profit':
            return item.totalResult;
          case 'craftDate':
            return item.craftDate ? new Date(item.craftDate).getTime() : 0;
          default:
            return '';
        }
      };

      ds.sort = doneCraftsSort;
    }

    const doneCraftsPaginator = this.doneCraftsPaginator();

    if (doneCraftsPaginator) {
      ds.paginator = doneCraftsPaginator;
    }

    return ds;
  }

  public deleteDoneCraft(doneCraft: DoneCraft) {
    if (!this.afmBeService.userHasMasterpieceEntitlement()) {
      return;
    }

    const dialogRef = this.matDialog.open(DialogComponent, {
      width: '250px',
      data: { title: 'Confirm Delete', message: `Are you sure you want to delete this craft? This can't be undone.` },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result && doneCraft.id) {
        this.craftCalculatorStateService.isDeletingDoneCraft.set(true);
        this.topItemsService
          .deleteDoneCraft(doneCraft.id)
          .pipe(
            take(1),
            finalize(() => this.craftCalculatorStateService.isDeletingDoneCraft.set(false))
          )
          .subscribe({
            next: () => {
              this.craftCalculatorStateService.userDoneCrafts.update((doneCrafts) => doneCrafts.filter((craft) => craft.id !== doneCraft.id));
              this.snackBar.open('Deleted Done Craft', undefined, { duration: 2000 });
            },
            error: (error) => {
              console.error('Error deleting done craft:', error);
              this.snackBar.open('Error deleting done craft', 'Close', { duration: 4000 });
            },
          });
      }
    });
  }

  public deleteCraftGroup(craftGroup: CraftGroup) {
    if (!this.afmBeService.userHasMasterpieceEntitlement()) {
      return;
    }
    const dialogRef = this.matDialog.open(DialogComponent, {
      width: '250px',
      data: { title: 'Confirm Delete', message: `Are you sure you want to delete this craft group? This can't be undone.` },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result && craftGroup.id()) {
        this.craftCalculatorStateService.isDeletingCraftGroup.set(true);
        this.topItemsService
          .deleteCraftGroup(craftGroup.id()!)
          .pipe(
            take(1),
            finalize(() => this.craftCalculatorStateService.isDeletingCraftGroup.set(false))
          )
          .subscribe({
            next: () => {
              this.craftCalculatorStateService.userCraftGroups.update((craftGroups) => craftGroups.filter((group) => group.id() !== craftGroup.id()));

              //check if the current craft group is the one being deleted, if it is, reset it's id so we can save a new one if needed
              if (this.craftCalculatorStateService.craftGroup().id() === craftGroup.id()) {
                this.craftCalculatorStateService.craftGroup().id.set(undefined);
              }

              this.snackBar.open('Deleted Craft Group', undefined, { duration: 2000 });
            },
            error: (error) => {
              console.error('Error deleting craft group:', error);
              this.snackBar.open('Error deleting craft group', 'Close', { duration: 4000 });
            },
          });
      }
    });
  }

  public saveCurrentCraftGroup = () => {
    if (!this.afmBeService.userHasMasterpieceEntitlement()) {
      return;
    }

    const craftGroup = this.craftCalculatorStateService.craftGroup();

    if (!!craftGroup.id()) {
      const dialogRef = this.matDialog.open(DialogComponent, {
        width: '250px',
        data: { title: 'Confirm Save', message: `This craft group already exists and will be overwritten. Do you want to continue?` },
      });
      dialogRef.afterClosed().subscribe(async (result) => {
        if (!result) {
          return;
        }
        this.performSave(craftGroup);
      });
    } else {
      this.performSave(craftGroup);
    }
  };

  private performSave(craftGroup: any) {
    this.craftCalculatorStateService.isSavingCraftGroup.set(true);

    const persistenceModel = CraftGroupPersistenceHelper.toPersistenceModel(craftGroup);

    this.topItemsService
      .saveCraftGroup(persistenceModel)
      .pipe(
        take(1),
        finalize(() => this.craftCalculatorStateService.isSavingCraftGroup.set(false))
      )
      .subscribe({
        next: (response) => {
          if (response?.craftGroup) {
            this.handleSuccessfulCraftGroupSave(response);
            this.snackBar.open('Saved Craft Group', undefined, { duration: 2000 });
          } else {
            console.error('Failed to save craft group: No craft group in response');
            this.snackBar.open('Failed to save craft group', 'Close', { duration: 4000 });
          }
        },
        error: (error) => {
          console.error('Error saving craft group:', error);
          this.snackBar.open('Error saving craft group', 'Close', { duration: 4000 });
        },
      });
  }

  public cloneCraftGroup(craftGroup: CraftGroup) {
    if (!this.afmBeService.userHasMasterpieceEntitlement()) {
      return;
    }
    // Create a clone by converting to persistence model and back
    const persistenceModel = CraftGroupPersistenceHelper.toPersistenceModel(craftGroup);
    const clonedCraftGroup = CraftGroupPersistenceHelper.fromPersistenceModel(
      persistenceModel,
      this.jsonReaderService,
      this.aoDataGetterService,
      this.craftCalculatorStateService.achievementsSignal,
      this.craftCalculatorStateService,
      getAllLocations(),
      getAllPriceTypes()
    );

    clonedCraftGroup.id.set(undefined);
    clonedCraftGroup.name.set(`New ${craftGroup.name()}`);

    this.craftCalculatorStateService.craftGroup.set(clonedCraftGroup);

    this.snackBar.open('Cloned Craft Group', undefined, { duration: 2000 });
  }

  public saveCurrentCraftGroupAsDoneCraft() {
    if (!this.afmBeService.userHasMasterpieceEntitlement()) {
      return;
    }
    this.craftCalculatorStateService.isSavingDoneCraft.set(true);

    const craftGroup = this.craftCalculatorStateService.craftGroup();
    const persistenceModel = toDoneCraftPersistenceModel(craftGroup);

    this.topItemsService
      .saveDoneCraft(persistenceModel)
      .pipe(
        take(1),
        finalize(() => this.craftCalculatorStateService.isSavingDoneCraft.set(false))
      )
      .subscribe({
        next: (response) => {
          if (response) {
            this.handleSuccesfulDoneCraftSave(response);
            this.snackBar.open('Saved Done Craft', undefined, { duration: 2000 });
          } else {
            console.error('Failed to save done craft: No done craft in response');
            this.snackBar.open('Failed to save done craft', 'Close', { duration: 4000 });
          }
        },
        error: (error) => {
          console.error('Error saving done craft:', error);
          this.snackBar.open('Error saving done craft', 'Close', { duration: 4000 });
        },
      });
  }

  private handleSuccesfulDoneCraftSave(response: DoneCraftPersistanceModel) {
    const returnedDoneCraft = fromDoneCraftPersistenceModel(
      response,
      this.jsonReaderService,
      this.aoDataGetterService,
      this.craftCalculatorStateService.achievementsSignal,
      this.craftCalculatorStateService,
      getAllLocations(),
      getAllPriceTypes()
    );

    this.craftCalculatorStateService.userDoneCrafts.update((doneCrafts) => [...doneCrafts, returnedDoneCraft]);
  }

  private handleSuccessfulCraftGroupSave(response: any) {
    const returnedCraftGroup = CraftGroupPersistenceHelper.fromPersistenceModel(
      response.craftGroup,
      this.jsonReaderService,
      this.aoDataGetterService,
      this.craftCalculatorStateService.achievementsSignal,
      this.craftCalculatorStateService,
      getAllLocations(),
      getAllPriceTypes()
    );

    this.craftCalculatorStateService.craftGroup.set(returnedCraftGroup);
    this.craftCalculatorStateService.updateUserCraftGroups(returnedCraftGroup, response.craftGroup.id);
  }
}
